import {
  CogIcon,
  DownloadIcon,
  InformationCircleIcon,
  FilterIcon,
} from "@heroicons/react/outline";
import Button from "components/Button";
import { PureTooltip } from "components/Tooltip";
import AnalyticsDatePicker from "./AnalyticsDatePicker";
import { ErrorLogFilterPills } from "./ActiveFilterPills";
import useIsMobile from "hooks/useIsMobile";
import { trackAnalyticsActionClicked } from "utils/Tracking";
import { SearchInput } from "components/SearchInput";

interface ErrorLogsFilterHeaderProps {
  onFiltersClick: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showHidden?: boolean;
  onHiddenToggle?: (show: boolean) => void;
  onCustomizeColumns?: () => void;
  onDownload?: () => void | Promise<void>;
  downloadButtonText?: string;
  disableDownload?: boolean;
  downloadLoading?: boolean;
  downloadLoadingText?: string;
}

const ErrorLogsFilterHeader: React.FC<ErrorLogsFilterHeaderProps> = ({
  onFiltersClick,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  showHidden,
  onHiddenToggle,
  onCustomizeColumns,
  onDownload,
  downloadButtonText = "DOWNLOAD LOGS",
  disableDownload = false,
  downloadLoading = false,
  downloadLoadingText = "DOWNLOADING...",
}) => {
  const isMobile = useIsMobile();
  const resolvedDownloadText = downloadLoading
    ? downloadLoadingText
    : downloadButtonText;

  return (
    <div className="flex flex-col gap-3">
      {/* Main filter bar - using flexbox for better control */}
      <div
        className="flex gap-3 flex-wrap"
      >
        {/* Filters button */}
        <Button
          icon={{ Icon: FilterIcon }}
          text="FILTERS"
          className="button button-primary h-10 px-4 flex items-center gap-2 rounded text-white shrink-0"
          onClick={onFiltersClick}
        />

        {/* Date Picker */}
        <AnalyticsDatePicker
          className="shrink-0"
          inputClassName="h-10"
        />

        {/* Search Input */}
        <SearchInput
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          placeholder={searchPlaceholder}
          className="w-[280px] max-w-full"
        />

        {/* Show hidden toggle */}
        <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                onHiddenToggle?.(!showHidden);
                trackAnalyticsActionClicked({ section: 'error_logs', action: showHidden ? 'hide_hidden_logs' : 'show_hidden_logs', target: 'error_logs_table' });
              }}
              aria-pressed={showHidden}
              className={`
                relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                flex-shrink-0
                ${showHidden ? "bg-indigo-600" : "bg-gray-200"}
              `}
            >
              <span
                className={`
                  absolute right-0.5 top-1/2 -translate-y-1/2 text-base font-bold text-white
                  transition-opacity duration-200 flex items-center justify-center w-4 h-4
                  ${showHidden ? "opacity-0" : "opacity-100"}
                `}
              >
                ×
              </span>
              <span
                className={`
                  absolute left-0.5 top-1/2 -translate-y-1/2 text-sm font-bold text-white
                  transition-opacity duration-200 flex items-center justify-center w-4 h-4
                  ${showHidden ? "opacity-100" : "opacity-0"}
                `}
              >
                ✓
              </span>
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0
                  transition duration-200 ease-in-out
                  ${showHidden ? "translate-x-5" : "translate-x-0"}
                `}
              />
            </button>
            <span className="text-sm text-gray-700 whitespace-nowrap sm:inline">
                {showHidden ? "Hidden rows visible" : "Hidden rows hidden"}
            </span>
          <PureTooltip
            content={
              showHidden
                ? "Hidden logs are currently visible"
                : "Show hidden logs"
            }
            className="text-gray-400 hidden sm:block"
          >
            <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
          </PureTooltip>
        </div>

        {/* Action buttons - right aligned */}
        <div className="flex items-center gap-2 ml-auto">
          {onCustomizeColumns && (
            <Button
              icon={{ Icon: CogIcon }}
              text={isMobile ? "" : "CUSTOMIZE COLUMNS"}
              className="button button-tertiary px-2 sm:px-3 text-xs sm:text-sm h-10 whitespace-nowrap"
              onClick={onCustomizeColumns}
            />
          )}
          {onDownload && (
            <Button
              icon={{ Icon: DownloadIcon }}
              text={isMobile ? "" : resolvedDownloadText}
              className="button button-tertiary px-2 sm:px-3 text-xs sm:text-sm h-10 whitespace-nowrap"
              onClick={onDownload}
              disabled={disableDownload || downloadLoading}
            />
          )}
        </div>
      </div>

      {/* Active Filter Pills */}
      <ErrorLogFilterPills />
    </div>
  );
};

export default ErrorLogsFilterHeader;
